import cardLindelof from "@/assets/objectives/card-lindelof.webp";
import cardRosa from "@/assets/objectives/card-rosa.webp";
import cardSavinho from "@/assets/objectives/card-savinho.webp";
import planScreenshotMobile from "@/assets/objectives/plan-mobile.webp";
import planScreenshot from "@/assets/objectives/plan-v2.webp";
import { alternateLinks, rootCanonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { cn } from "@/lib/utils";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { ReactNode, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

import { ObjectivesFaq } from "./ObjectivesFaq";
import { PickDemo } from "./PickDemo";
import { RewardsCarousel } from "./RewardsCarousel";
import { WaitlistForm } from "./WaitlistForm";
import { useObjectivesWaitlist } from "./useObjectivesWaitlist";

const STEPS = [
  { id: "pick", number: "01" },
  { id: "plan", number: "02" },
  { id: "squad", number: "03" },
] as const;

const PLAN_CHIPS = [
  { key: "chipOrder" },
  { key: "chipProgress" },
  { key: "chipRequirements" },
] as const;

// Blurred on purpose: the squad stays hidden until launch.
const SQUAD_ROWS = [
  {
    cards: [cardSavinho, cardRosa, cardLindelof],
    gap: "gap-6 mobile:gap-[60px]",
  },
  {
    cards: [cardLindelof, cardSavinho, cardRosa],
    gap: "gap-8 mobile:gap-[90px]",
  },
  {
    cards: [cardRosa, cardLindelof, cardSavinho, cardRosa],
    gap: "gap-3 mobile:gap-[30px]",
  },
];

const scrollToStep = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const ObjectivesLandingPage = () => {
  const { t } = useTranslation("objectives");
  const waitlist = useObjectivesWaitlist();
  const canonical = rootCanonicalUrl("/objectives");

  return (
    <main className="flex-1 bg-primary-dark px-4 pb-24 text-neutral-100 mobile:px-12">
      <Helmet>
        <title>{`${t("landing.seo.title", { game: GAME_NAME })}`}</title>
        <meta
          name="description"
          content={t("landing.seo.description", { game: GAME_NAME })}
        />
        <link rel="canonical" href={canonical} />
        {alternateLinks(canonical)}
      </Helmet>

      <section className="flex flex-col items-center pt-14 text-center touch-device:pt-[88px]">
        <div className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-2">
          <span className="text-sm font-semibold uppercase tracking-[.06em] text-neutral-200">
            {t("landing.hero.product")}
          </span>
          <span className="rounded-full bg-primary-action/12 px-3 py-1.5 text-xs font-semibold uppercase tracking-[.06em] text-primary-washed-blue">
            {t("landing.hero.eyebrow")}
          </span>
        </div>
        <h1 className="mt-[22px] max-w-[900px] text-balance text-4xl font-extrabold leading-[1.02] tracking-[-.03em] mobile:text-5xl touch-device:text-[64px]">
          {t("landing.hero.title")}
        </h1>
        <h2 className="mt-[22px] max-w-[680px] text-pretty text-lg leading-normal text-neutral-400 mobile:text-xl">
          {t("landing.hero.subtitle")}
        </h2>
        <WaitlistForm
          form="hero"
          waitlist={waitlist}
          className="mt-9 w-full max-w-[520px]"
        />
      </section>

      {/* Left-aligned, unlike the centred hero, so it reads as a new section. */}
      <section className="mx-auto mt-20 max-w-[1120px]">
        <SectionTitle>{t("landing.rewards.title")}</SectionTitle>
        <p className="mt-3 text-pretty text-base text-neutral-400">
          {t("landing.rewards.subtitle")}
        </p>
        <RewardsCarousel className="mt-9" />
      </section>

      <nav className="mx-auto mt-6 grid max-w-[1184px] grid-cols-1 border-t border-white/8 mobile:grid-cols-3">
        {STEPS.map(({ id, number }) => (
          <button
            key={id}
            type="button"
            onClick={() => scrollToStep(id)}
            className="flex gap-3.5 pt-[22px] text-left mobile:px-6 mobile:first:pl-0 mobile:last:pr-0"
          >
            <span className="font-oswald text-sm font-semibold text-primary-action">
              {number}
            </span>
            <span className="text-[15px] font-semibold">
              {t(`landing.jumps.${id}`)}
            </span>
          </button>
        ))}
      </nav>

      <div className="mx-auto max-w-[1184px]">
        <section
          id="pick"
          className="mt-24 grid scroll-mt-20 items-center gap-10 touch-device:mt-32 touch-device:grid-cols-[420px_1fr] touch-device:gap-16"
        >
          <StepText
            number="01"
            title={t("landing.pick.title")}
            body={t("landing.pick.body")}
          />
          <PickDemo />
        </section>

        <section
          id="plan"
          className="mt-24 flex scroll-mt-20 flex-col items-center text-center touch-device:mt-36"
        >
          <StepText
            number="02"
            title={t("landing.plan.title")}
            body={t("landing.plan.body")}
            className="max-w-[760px] [&_p]:mx-auto [&_p]:max-w-[620px]"
          />
          <div className="mt-7 flex flex-wrap justify-center gap-2.5">
            {PLAN_CHIPS.map(({ key }) => (
              <span
                key={key}
                className="inline-flex items-center gap-2 rounded-full bg-neutral-800 px-3.5 py-2 text-[13px] font-semibold text-neutral-300"
              >
                <span className="size-1.5 rounded-full bg-primary-action" />
                {t(`landing.plan.${key}`)}
              </span>
            ))}
          </div>
          <div className="mt-11 w-full max-w-[1120px] overflow-hidden rounded-[14px] border border-white/8 bg-neutral-900">
            {/* Below the `mobile` breakpoint (769px) the wide plan shrinks
                past legibility, so phones get a tall crop of one step. */}
            <picture>
              <source
                media="(max-width: 768.98px)"
                srcSet={planScreenshotMobile}
              />
              <img
                src={planScreenshot}
                alt={t("landing.plan.alt")}
                loading="lazy"
                className="block h-auto w-full [mask-image:linear-gradient(to_bottom,#000_70%,transparent)]"
              />
            </picture>
          </div>
        </section>

        <section
          id="squad"
          className="mt-24 grid scroll-mt-20 items-center gap-10 touch-device:mt-36 touch-device:grid-cols-[1fr_420px] touch-device:gap-16"
        >
          <SquadTeaser />
          <StepText
            number="03"
            title={t("landing.squad.title")}
            body={t("landing.squad.body")}
            className="order-first touch-device:order-none"
          />
        </section>

        <section className="mt-24 grid items-start gap-10 touch-device:mt-36 touch-device:grid-cols-[380px_1fr] touch-device:gap-16">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[.08em] text-complete">
              {t("landing.faq.eyebrow")}
            </span>
            <SectionTitle>{t("landing.faq.title")}</SectionTitle>
            <p className="mt-[18px] text-pretty text-base leading-relaxed text-neutral-400">
              {t("landing.faq.body")}
            </p>
          </div>
          <ObjectivesFaq />
        </section>

        <section className="mt-24 flex flex-col items-center rounded-2xl border border-white/6 bg-[radial-gradient(600px_240px_at_50%_0%,rgba(32,159,249,.16),transparent_70%),#1c1c1c] px-6 py-14 text-center touch-device:mt-36 touch-device:px-14 touch-device:py-[72px]">
          <h2 className="text-balance text-3xl font-extrabold leading-[1.05] tracking-[-.03em] mobile:text-[44px]">
            {t("landing.cta.title")}
          </h2>
          <WaitlistForm
            form="bottom"
            waitlist={waitlist}
            className="mt-8 w-full max-w-[520px]"
          />
        </section>
      </div>
    </main>
  );
};

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <h2 className="mt-3.5 text-balance text-3xl font-extrabold leading-[1.08] tracking-[-.025em] mobile:text-[40px]">
    {children}
  </h2>
);

const StepText = ({
  number,
  title,
  body,
  className,
}: {
  number: string;
  title: string;
  body: string;
  className?: string;
}) => {
  const { t } = useTranslation("objectives");
  return (
    <div className={className}>
      <span className="font-oswald text-sm font-semibold uppercase tracking-[.12em] text-primary-action">
        {t("landing.step", { number })}
      </span>
      <SectionTitle>{title}</SectionTitle>
      <p className="mt-[18px] text-pretty text-[17px] leading-relaxed text-neutral-400">
        {body}
      </p>
    </div>
  );
};

const SquadTeaser = () => {
  const { t } = useTranslation("objectives");
  return (
    <div className="relative h-[340px] overflow-hidden rounded-2xl border border-white/8 bg-[radial-gradient(420px_260px_at_50%_60%,rgba(32,159,249,.14),transparent_70%),#1a1a1a] mobile:h-[440px]">
      <div className="absolute inset-7 rounded-[10px] border border-white/6" />
      <div className="absolute bottom-7 left-1/2 top-7 w-px bg-white/6" />
      <div className="absolute left-1/2 top-1/2 size-[110px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/6" />
      <div
        aria-hidden
        className="absolute inset-0 grid grid-rows-3 px-4 py-10 opacity-80 blur-[7px] mobile:px-[60px]"
      >
        {SQUAD_ROWS.map((row, r) => (
          <div
            key={r}
            className={cn(
              "flex justify-center",
              row.gap,
              r === 1 && "items-center",
              r === 2 && "items-end",
            )}
          >
            {row.cards.map((src, c) => (
              <img key={c} src={src} alt="" className="h-20 mobile:h-[104px]" />
            ))}
          </div>
        ))}
      </div>
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-4 bg-gradient-to-t from-[rgba(20,20,20,.95)] to-transparent px-[22px] py-[18px]">
        <span className="text-sm font-bold">
          {t("landing.squad.game", {
            number: 8,
            name: "Ones We Watched Pro Challenge",
          })}
        </span>
        <span className="shrink-0 text-[13px] text-neutral-400">
          {t("landing.squad.fromClub", { count: 11, total: 11 })}
        </span>
      </div>
    </div>
  );
};

export default ObjectivesLandingPage;
